import type { GroupRole } from 'bundles/naptimejs/resources/__generated__/GroupMembershipsV2';

import _t from 'i18n!nls/authoring';

type UserData = {
  definition: { fullName?: string; firstName?: string; lastName?: string; externalId?: string; email?: string };
};

class GroupMember {
  declare id: string;

  declare groupId?: string;

  declare userId: number;

  /** public (hashed) coursera userId */
  declare externalUserId: string;

  declare role?: GroupRole;

  declare createdAt?: number;

  declare userData?: UserData | null;

  declare isViewAsLearnerMembership?: boolean;

  declare isStaffMember?: boolean;

  constructor({
    id,
    groupId,
    userId,
    externalUserId = '',
    role,
    createdAt,
    userData,
    fullName,
    email,
    externalId,
    isViewAsLearnerMembership,
    isStaff: isStaffMember, // comes from resources calling TeamworkUserProfilesV1 for team member profile information
  }: {
    id: string;
    groupId?: string;
    userId: number;
    externalUserId?: string;
    role?: GroupRole;
    createdAt?: number;
    userData?: UserData;
    fullName?: string;
    email?: string;
    externalId?: string;
    isViewAsLearnerMembership?: boolean;
    isStaff?: boolean;
  }) {
    this.id = id;
    this.groupId = groupId;
    this.userId = userId;
    this.externalUserId = externalUserId;
    this.role = role;
    this.createdAt = createdAt;
    this.userData = userData;
    this.isViewAsLearnerMembership = isViewAsLearnerMembership;
    this.isStaffMember = isStaffMember;

    // this is for ease of building a GroupMember model when you have partial userData
    if (!userData) {
      this.userData = { definition: {} };
      if (fullName) {
        this.userData.definition.fullName = fullName;
      }
      if (email) {
        this.userData.definition.email = email;
      }
      if (externalId) {
        this.userData.definition.externalId = externalId;
      }
    }
  }

  get fullName() {
    return this.userData && this.userData.definition.fullName;
  }

  get firstName() {
    return this.userData?.definition?.firstName;
  }

  get lastName() {
    return this.userData?.definition?.lastName;
  }

  get email() {
    return this.userData && this.userData.definition.email;
  }

  get isStaff() {
    return this.isViewAsLearnerMembership || this.isStaffMember;
  }

  /**
   * Note: externalId is a property of group membership that corresponds to a learner id provided by the partner
   * at the time of group membership creation. For example, this may be their university student id.
   */
  get externalId() {
    return this.userData && this.userData.definition.externalId;
  }

  memberHasFirstLastName() {
    return this.userData?.definition?.firstName?.trim() && this.userData?.definition?.lastName?.trim();
  }

  /**
   * @param {boolean} isAnonymized  whether name should be anonymized
   * @param {number} memberIndex one-based index for anonymous learner, use when there's a list of anon learners
   */
  getDisplayName(isAnonymized?: boolean | null, memberIndex?: number) {
    const userData = this.userData?.definition;
    if (isAnonymized) {
      if (userData?.externalId) {
        return userData.externalId;
      } else if (typeof memberIndex === 'number') {
        return _t('Anonymous Learner #{memberIndex}', { memberIndex });
      }

      return _t('Anonymous Learner');
    } else {
      return userData?.fullName?.trim() || _t('(No Name)');
    }
  }

  /**
   * @param {boolean} isAnonymized  whether name should be anonymized
   * @param {number} memberIndex one-based index for anonymous learner, use when there's a list of anon learners
   */
  getFirstName(isAnonymized?: boolean, memberIndex?: number) {
    return (
      (!isAnonymized && this.userData?.definition?.firstName?.trim()) || this.getDisplayName(isAnonymized, memberIndex)
    );
  }

  /**
   * @param {boolean} isAnonymized  whether name should be anonymized
   */
  getLastName(isAnonymized: boolean) {
    return (!isAnonymized && this.userData?.definition?.lastName?.trim()) || '';
  }
}

export default GroupMember;
